// A mesma licitação pelas duas fontes (07/10/2026).
//
// Lucas: "precisamos que o sistema já junte oportunidades que são iguais vindas
// do PNCP e do Conlicitação. Também que utilize o que há de mais atualizado de
// informação de uma e de outra." Quando o PNCP e a ConLicitação trazem o mesmo
// edital, o banco junta as duas linhas (migration 1800900000000): a principal é
// a que aparece e recebe as decisões; a gêmea fica guardada, ligada a ela, e o
// que a fonte da gêmea trouxer de novo (prorrogação, suspensão, valor) vale aqui.
// Este aviso conta isso na ficha e deixa desfazer quando a junção errou.
import { Alert, Button, Popconfirm, Space, Typography, App as AntApp } from 'antd';
import { useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../config';
import { getToken } from '../../utils/auth';

const { Text } = Typography;

export interface Gemea {
  id: number;
  fonte: string | null;
  numero_edital?: string | null;
  situacao?: string | null;
  link_edital?: string | null;
  conlic_url?: string | null;
  prazo_da_fonte?: string | null;
  unificada_regra?: string | null;
}

export const nomeDaFonte = (f?: string | null) =>
  f === 'conlicitacao' ? 'ConLicitação' : f === 'pncp' ? 'PNCP' : f === 'manual' ? 'cadastro manual' : (f || 'outra fonte');

const COMO: Record<string, string> = {
  controle_pncp: 'pelo número de controle do PNCP',
  link_pncp: 'pelo link do PNCP',
  mesmo_edital: 'pelo mesmo órgão, número do edital, data e valor',
  manual: 'por uma pessoa',
};

/** "13/10 09:00" a partir de parede de Brasília sem offset. */
function quando(s?: string | null): string | null {
  const m = String(s || '').match(/^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})/);
  return m ? `${m[3]}/${m[2]}/${m[1]} ${m[4]}:${m[5]}` : null;
}

export function GemeasDaLicitacao({ gemeas, redirecionadaDe, onSeparado }: {
  gemeas?: Gemea[] | null;
  redirecionadaDe?: number | null;
  onSeparado?: () => void;
}) {
  const { message } = AntApp.useApp();
  const [separando, setSeparando] = useState<number | null>(null);
  const lista = Array.isArray(gemeas) ? gemeas : [];
  if (!lista.length && !redirecionadaDe) return null;

  async function separar(g: Gemea) {
    setSeparando(g.id);
    try {
      const token = await getToken();
      const r = await axios.post(`${API_URL}/comercial/licitacoes`, { action: 'separar_fonte', id: g.id },
        { headers: { Authorization: token } });
      const d = r.data?.body ? JSON.parse(r.data.body) : r.data;
      if (d?.error) throw new Error(d.error);
      // Volta ao estado de ANTES da junção: se já estava arquivada (vencida,
      // descartada), continua — e a mensagem diz isso, em vez de "voltou".
      message.success(d?.continua_arquivada
        ? `Separadas: a #${g.id} (${nomeDaFonte(g.fonte)}) é outra licitação e continua arquivada, como estava antes de ser juntada (veja em "Mostrar arquivadas").`
        : `Separadas: a #${g.id} (${nomeDaFonte(g.fonte)}) voltou a ser uma licitação à parte.`);
      onSeparado?.();
    } catch (e: any) {
      message.error(e?.response?.data?.error || e.message || 'Não deu para separar.');
    } finally { setSeparando(null); }
  }

  return (
    <Alert type="info" showIcon style={{ marginTop: 10 }}
      message={lista.length
        ? `Esta licitação veio também pel${lista.length === 1 && lista[0].fonte === 'pncp' ? 'o' : 'a'} ${lista.map(g => nomeDaFonte(g.fonte)).join(' e ')} — as duas foram juntadas`
        : 'Licitação juntada'}
      description={(
        <Space direction="vertical" size={4} style={{ width: '100%' }}>
          {redirecionadaDe && (
            <Text style={{ fontSize: 12 }}>
              Você abriu a #{redirecionadaDe}, que é a mesma licitação desta e foi juntada a ela.
            </Text>
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>
            Prazo, situação e valor mostrados aqui são o dado mais atualizado entre as fontes; as alterações que
            chegarem por qualquer uma aparecem nesta ficha.
          </Text>
          {lista.map(g => {
            const link = g.fonte === 'pncp' ? (g.conlic_url || g.link_edital) : g.link_edital;
            const prazo = quando(g.prazo_da_fonte);
            return (
              <Space key={g.id} size={8} wrap style={{ fontSize: 12 }}>
                <Text style={{ fontSize: 12 }}>
                  <b>{nomeDaFonte(g.fonte)}</b> #{g.id}{g.numero_edital ? ` · edital ${g.numero_edital}` : ''}
                  {g.situacao ? ` · ${g.situacao}` : ''}{prazo ? ` · prazo na fonte ${prazo}` : ''}
                  {g.unificada_regra && COMO[g.unificada_regra] ? ` · juntada ${COMO[g.unificada_regra]}` : ''}
                </Text>
                {link && /^https?:\/\//.test(link) && (
                  <a href={link} target="_blank" rel="noreferrer">ver na {nomeDaFonte(g.fonte)}</a>
                )}
                <Popconfirm title="Não é a mesma licitação?"
                  description={`A #${g.id} volta a ser uma licitação à parte (como estava antes) e o sistema não junta as duas de novo.`}
                  okText="Separar" cancelText="Cancelar" onConfirm={() => separar(g)}>
                  <Button size="small" type="link" danger loading={separando === g.id} style={{ padding: 0 }}>
                    não é a mesma — separar
                  </Button>
                </Popconfirm>
              </Space>
            );
          })}
        </Space>
      )} />
  );
}
